import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Check, Close, OpenInNew, PictureAsPdf } from '@mui/icons-material';
import { useNavigate as useRouterNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { approvalsAPI, attachmentsAPI, useNotification, PageHeader, PageContent } from '@viilutalous/common';
import { useTenant } from '../contexts/TenantContext';
import { useMeHost } from '../contexts/MeHostContext';
import { format } from 'date-fns';
import {
  createPreviewLoader,
  keepIfActive,
  loadInvoiceFullPreview,
  previewKey,
  revokePreviewUrls,
  uniquePurchaseInvoiceTargets,
} from './myApprovalsPreview';

function statusColor(status) {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  return 'warning';
}

const ENTITY_PATH = {
  purchase_invoice: 'purchase-invoices',
};

const thumbSx = {
  width: 56,
  height: 72,
  p: 0,
  overflow: 'hidden',
  cursor: 'pointer',
  border: 1,
  borderColor: 'divider',
  borderRadius: 0.5,
  bgcolor: 'grey.100',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// Asks for the row's thumbnail once the cell scrolls into view.
function useRequestWhenVisible(req, onVisible) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || !req) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      onVisible(req);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        onVisible(req);
      }
    }, { rootMargin: '200px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [req, onVisible]);
  return ref;
}

function InvoiceImageCell({ req, preview, label, onOpen, onVisible }) {
  const ref = useRequestWhenVisible(req, onVisible);
  return (
    <Box ref={ref} sx={{ minHeight: 24, display: 'flex', alignItems: 'center' }}>
      <InvoiceImageButton preview={preview} label={label} onOpen={onOpen} />
    </Box>
  );
}

function InvoiceImageButton({ preview, label, onOpen }) {
  if (!preview || (preview.kind === 'image' && !preview.url)) {
    return '—';
  }
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={label}
      onClick={() => onOpen(preview)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(preview);
        }
      }}
      sx={thumbSx}
    >
      {preview.kind === 'image' ? (
        <Box component="img" src={preview.url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <PictureAsPdf fontSize="small" color="action" />
      )}
    </Box>
  );
}

export default function MyApprovals() {
  const { t } = useTranslation();
  const navigate = useRouterNavigate();
  const { tenants, selectTenant } = useTenant();
  const { hostId, hosts } = useMeHost();
  const { showError } = useNotification();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [previews, setPreviews] = useState({});
  const [openPreview, setOpenPreview] = useState(null);
  const previewsRef = useRef({});
  const openPreviewRef = useRef(null);
  const loaderRef = useRef(null);
  const mountedRef = useRef(false);

  const tenantById = useMemo(() => {
    const m = new Map();
    for (const tnt of tenants) m.set(tnt.id, tnt);
    return m;
  }, [tenants]);

  const load = useCallback(async () => {
    if (!hostId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await approvalsAPI.getPendingAcrossHost(hostId, {
        status: showCompleted ? 'all' : 'pending',
      });
      setRows(res.data || []);
    } catch (err) {
      showError(err?.response?.data?.detail || t('common.error'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [hostId, showCompleted, showError, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    previewsRef.current = previews;
  }, [previews]);

  useEffect(() => {
    openPreviewRef.current = openPreview;
  }, [openPreview]);

  // Created on first use: row cells run their effects before this component does.
  const getLoader = useCallback(() => {
    if (!loaderRef.current) {
      loaderRef.current = createPreviewLoader(attachmentsAPI, {
        onPreview: (key, preview) => {
          setPreviews((prev) => {
            if (prev[key]) {
              revokePreviewUrls(preview);
              return prev;
            }
            const next = { ...prev, [key]: preview };
            previewsRef.current = next;
            return next;
          });
        },
      });
    }
    return loaderRef.current;
  }, []);

  const requestPreview = useCallback((req) => getLoader().request(req), [getLoader]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Drop the stopped loader: StrictMode and fast refresh mount this page again,
      // and the next getLoader() call must start a fresh queue.
      loaderRef.current?.stop();
      loaderRef.current = null;
      Object.values(previewsRef.current).forEach(revokePreviewUrls);
    };
  }, []);

  useEffect(() => {
    const needed = new Set(uniquePurchaseInvoiceTargets(rows).map(previewKey));
    const openKey = openPreviewRef.current?.key;

    setPreviews((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (needed.has(key) || key === openKey) return;
        revokePreviewUrls(next[key]);
        delete next[key];
      });
      previewsRef.current = next;
      return next;
    });
    getLoader().retain(needed);
  }, [rows, getLoader]);

  const handleApprove = async (req) => {
    setSubmitting(true);
    try {
      await approvalsAPI.approve(req.tenant_id, req.id, null);
      await load();
    } catch (err) {
      showError(err?.response?.data?.detail || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const openReject = (req) => {
    setRejectComment('');
    setRejectDialog(req);
  };

  const handleRejectConfirm = async () => {
    if (!rejectDialog || !rejectComment.trim()) return;
    setSubmitting(true);
    try {
      await approvalsAPI.reject(rejectDialog.tenant_id, rejectDialog.id, rejectComment.trim());
      setRejectDialog(null);
      await load();
    } catch (err) {
      showError(err?.response?.data?.detail || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenPreview = async (preview) => {
    if (preview.url) {
      setOpenPreview(preview);
      return;
    }
    setOpenPreview({ ...preview, loading: true });
    // The dialog may be closed, or another row opened, before the file arrives.
    const stillWaiting = () => openPreviewRef.current?.loading && openPreviewRef.current.key === preview.key;
    try {
      const full = keepIfActive(
        await loadInvoiceFullPreview(preview, attachmentsAPI),
        () => mountedRef.current,
      );
      if (!mountedRef.current) return;
      if (!full?.url) {
        // The stored file is not the PDF or image it claims to be.
        if (stillWaiting()) {
          setOpenPreview(null);
          showError(t('me.approvals.preview_unavailable'));
        }
        return;
      }
      setPreviews((prev) => {
        const next = { ...prev, [preview.key]: full };
        previewsRef.current = next;
        return next;
      });
      if (stillWaiting()) setOpenPreview(full);
    } catch (err) {
      if (!mountedRef.current || !stillWaiting()) return;
      setOpenPreview(null);
      // The file call returns a blob, so the API's error detail is not readable here.
      showError(t('me.approvals.preview_unavailable'));
    }
  };

  const openEntity = (req) => {
    const slug = req.tenant_slug || tenantById.get(req.tenant_id)?.slug;
    if (!slug) return;
    const path = ENTITY_PATH[req.entity_type];
    if (!path) return;
    // Switch tenant context so the destination page renders against the right tenant.
    const tnt = tenantById.get(req.tenant_id);
    if (tnt) selectTenant(tnt);
    navigate(`/app/${slug}/${path}/${req.entity_id}`);
  };

  return (
    <>
      <PageHeader />
      <PageContent>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                size="small"
              />
            }
            label={t('purchase_invoices.show_completed_approvals', 'Show completed')}
          />
          <Box sx={{ flexGrow: 1 }} />
          {loading && <CircularProgress size={20} />}
        </Stack>

        {hosts.length === 0 && (
          <Alert severity="info">{t('me.approvals.no_hosts')}</Alert>
        )}

        {hosts.length > 0 && rows.length === 0 && !loading && (
          <Alert severity="success">
            {t('me.approvals.empty')}
          </Alert>
        )}

        {rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('me.approvals.tenant')}</TableCell>
                <TableCell>{t('purchase_invoices.approval_step', 'Step')}</TableCell>
                <TableCell>{t('me.approvals.entity')}</TableCell>
                <TableCell>{t('me.approvals.invoice_image')}</TableCell>
                <TableCell>{t('common.created_at', 'Created')}</TableCell>
                <TableCell>{t('common.status', 'Status')}</TableCell>
                <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((req) => {
                const isPending = req.status === 'pending';
                return (
                  <TableRow key={req.id} hover>
                    <TableCell>
                      {req.tenant_name || tenantById.get(req.tenant_id)?.name || req.tenant_id}
                    </TableCell>
                    <TableCell>
                      {req.step_name
                        ? `${t('common.approval.step_n', { n: req.step_order })}: ${req.step_name}`
                        : t('common.approval.step_n', { n: req.step_order })}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        endIcon={<OpenInNew fontSize="inherit" />}
                        onClick={() => openEntity(req)}
                      >
                        {req.entity_type}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <InvoiceImageCell
                        req={req.entity_type === 'purchase_invoice' ? req : null}
                        preview={req.entity_type === 'purchase_invoice' ? previews[previewKey(req)] : null}
                        label={t('me.approvals.invoice_image')}
                        onOpen={handleOpenPreview}
                        onVisible={requestPreview}
                      />
                    </TableCell>
                    <TableCell>
                      {req.created_at ? format(new Date(req.created_at), 'dd.MM.yyyy HH:mm') : ''}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t(`common.approval.${req.status}`, req.status)}
                        color={statusColor(req.status)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<Check />}
                        onClick={() => handleApprove(req)}
                        disabled={!isPending || submitting}
                        sx={{ mr: 0.5 }}
                      >
                        {t('common.approval.approve', 'Approve')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Close />}
                        onClick={() => openReject(req)}
                        disabled={!isPending || submitting}
                      >
                        {t('common.approval.reject', 'Reject')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </PageContent>

      <Dialog open={!!openPreview} onClose={() => setOpenPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t('me.approvals.invoice_image')}</DialogTitle>
        <DialogContent>
          {openPreview?.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {openPreview && !openPreview.loading && openPreview.kind === 'image' && (
            <Box
              component="img"
              src={openPreview.url}
              alt=""
              sx={{ width: '100%', maxHeight: '75vh', objectFit: 'contain' }}
            />
          )}
          {/* No sandbox: Chromium leaves a PDF blank in a sandboxed frame. The blob is
              only built from bytes that start with %PDF and is typed application/pdf, so
              the browser hands it to its PDF viewer and never parses it as HTML. */}
          {openPreview && !openPreview.loading && openPreview.kind === 'pdf' && openPreview.url && (
            <iframe
              src={openPreview.url}
              title={t('me.approvals.invoice_image')}
              style={{ width: '100%', height: '75vh', border: 'none' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreview(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('common.approval.reject', 'Reject')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            label={t('common.approval.reject_comment', 'Reason')}
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            required
            error={rejectComment.trim() === ''}
            helperText={
              rejectComment.trim() === ''
                ? t('common.approval.comment_required', 'A comment is required')
                : ''
            }
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(null)} disabled={submitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleRejectConfirm}
            color="error"
            variant="contained"
            disabled={submitting || !rejectComment.trim()}
          >
            {submitting ? <CircularProgress size={18} /> : t('common.approval.reject', 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
